/**
 * The booking blacklist gate: what a refusal looks like, what gets recorded,
 * and the gate the edge checkout functions run.
 *
 * Why the edge needs its own gate: the browser books Defera Zero, Defera
 * Capital (Stripe) and Defera Capital (NMI) straight against Supabase Edge
 * (EDGE_ROUTES in src/lib/apiUrls.js), so the Express gates in server.js never
 * see those checkouts. Before this, the only thing in front of the three rails
 * that carry real loss was the Review pre-check — client-side and fail-open.
 * And because auto-default blacklists the travelers of a defaulted trip, the
 * people on the list are mostly people who just defaulted.
 *
 * The rules are blacklist-matching.js, the same file the Express gate runs;
 * the refusal body and the incident row below are shared with it too, so a
 * customer and an admin see the same thing whichever runtime refused.
 *
 * Fails CLOSED: when the travelers can't be screened, the checkout is refused
 * (503 — nothing has been charged yet, so the customer is told so and can try
 * again). The read hits the same Postgres the checkout writes to moments
 * later, so a checkout that can't be screened was about to fail anyway;
 * letting it through would quietly reopen the hole.
 *
 * Gate booking CREATION only, before the first write or charge. Finalize calls
 * (after 3D Secure) and every servicing path act on money that has already
 * moved; refusing those strands a customer mid-plan, which helps nobody.
 */
import { screenPassengers, supabaseBlacklistRowFetcher } from './blacklist-matching.js';

export const BOOKING_BLOCKED_CODE = 'BLACKLISTED';
/** Reason-free by design, like the Review block modal and the concierge's
 *  mandated phrasing: a restriction can exist for reasons that have nothing to
 *  do with payment history, so this must never imply one. */
export const BOOKING_BLOCKED_MESSAGE = "This account isn't able to place new bookings right now. Please contact support.";

export const BOOKING_SCREEN_FAILED_CODE = 'BOOKING_SCREEN_UNAVAILABLE';
/** The gate runs before any charge, so "not charged" is always true here. */
export const BOOKING_SCREEN_FAILED_MESSAGE = "We couldn't start your checkout just now. Please try again in a moment — your card was not charged.";

export const bookingBlockedBody = () => ({ success: false, error: BOOKING_BLOCKED_MESSAGE, code: BOOKING_BLOCKED_CODE });
export const bookingScreenFailedBody = () => ({ success: false, error: BOOKING_SCREEN_FAILED_MESSAGE, code: BOOKING_SCREEN_FAILED_CODE });

/** The gate refused this checkout before any charge: terminal, nothing to poll for. */
export function isBookingGateRefusal(body) {
  return body?.code === BOOKING_BLOCKED_CODE || body?.code === BOOKING_SCREEN_FAILED_CODE;
}

/** The blacklist_incidents row for a blocked attempt — admin-only, so it keeps
 *  the matched values and the travelers as submitted. */
export function blacklistIncidentRow({
  userId = null, userEmail = null, abandonedTripId = null, bookingId = null,
  passengers = null, identifiers = [], matches = [], ip = null, userAgent = null,
} = {}) {
  const rawByKey = new Map();
  for (const i of identifiers || []) {
    const k = `${i.identifier_type}::${i.normalized_value}`;
    if (!rawByKey.has(k)) rawByKey.set(k, i.raw_value || null);
  }
  return {
    user_id: userId,
    user_email: userEmail,
    abandoned_trip_id: abandonedTripId,
    booking_id: bookingId,
    matched: (matches || []).map((m) => ({
      identifier_type: m.identifier_type,
      normalized_value: m.normalized_value,
      raw_value_in_request: rawByKey.get(`${m.identifier_type}::${m.normalized_value}`) || null,
      blacklist_identifier_id: m.id,
      reason: m.reason || null,
    })),
    passengers,
    ip,
    user_agent: userAgent,
  };
}

const errorText = (err) => (err instanceof Error ? err.message : String(err));

/**
 * The gate for the edge checkout functions. Call it after the request is
 * validated and before anything is written or charged.
 *
 * @param {object} args
 * @param {object} args.supabase      service-role supabase-js client
 * @param {Request} args.req          the incoming request (IP + user agent for the incident)
 * @param {{ id?: string, email?: string | null }} args.user  the authenticated customer
 * @param {unknown} args.passengers   the travelers being booked, as submitted
 * @param {Record<string, string>} [args.corsHeaders]
 * @param {string} [args.logPrefix]
 * @returns {Promise<Response | null>} a Response to return as-is (403 restricted,
 *   503 couldn't screen), or null when the checkout may go ahead.
 */
export async function bookingBlacklistGate({
  supabase, req, user, passengers, corsHeaders = {}, logPrefix = 'booking-blacklist-gate',
}) {
  const respond = (status, body) => new Response(JSON.stringify(body), {
    status,
    headers: { ...corsHeaders, 'Content-Type': 'application/json' },
  });

  let screening;
  try {
    screening = await screenPassengers({ passengers, fetchRows: supabaseBlacklistRowFetcher(supabase) });
  } catch (err) {
    console.error(`${logPrefix}: blacklist screening failed — refusing checkout (fail closed)`, errorText(err));
    return respond(503, bookingScreenFailedBody());
  }
  if (!screening.blocked) return null;

  const headers = req?.headers;
  const row = blacklistIncidentRow({
    userId: user?.id ?? null,
    userEmail: user?.email ?? null,
    passengers: screening.passengers,
    identifiers: screening.identifiers,
    matches: screening.matches,
    ip: headers?.get?.('x-forwarded-for')?.split(',')[0]?.trim() || null,
    userAgent: headers?.get?.('user-agent') || null,
  });
  // The refusal stands whether or not this lands: the incident is the admins'
  // audit trail, not the gate. The log line carries no traveler details.
  try {
    const { error } = await supabase.from('blacklist_incidents').insert(row);
    if (error) throw new Error(error.message);
  } catch (err) {
    console.error(`${logPrefix}: blacklist incident not recorded`, {
      user_id: row.user_id, matched_types: screening.matchedTypes, error: errorText(err),
    });
  }
  console.warn(`${logPrefix}: booking refused — a traveler is restricted`, {
    user_id: row.user_id, matched_types: screening.matchedTypes,
  });
  return respond(403, bookingBlockedBody());
}
